import { getBaseUrl } from '@/lib/base-url';
import { createClient } from '@/lib/supabase/client';
import { getSupabaseAdminClient } from '@/lib/supabase/admin';
import { inChunks, selectAllRows } from '@/lib/supabase/batchedQueries';
import { Database, Json } from '@/types/supabase';
import { SupabaseClient } from '@supabase/supabase-js';
import type { CourtDefinition } from '@/app/[locale]/admin/event-management/[eventId]/edit/scheduler/match-scheduler-types';
import {
  hidesRegistrantsFromPublic,
  type RegistrantVisibilityEvent,
} from '@/app/[locale]/event/[eventId]/libs/registrantVisibility';
import { gateRowAliases } from '@/app/[locale]/event/[eventId]/libs/aliasGate';

// Types
type Event = Database['public']['Tables']['event']['Row'];
type EventInsert = Database['public']['Tables']['event']['Insert'];
type EventUpdate = Database['public']['Tables']['event']['Update'];

// Event Match Schedule types
type EventMatchSchedule = Database['public']['Tables']['event_match_schedule']['Row'];
type Player = Database['public']['Tables']['player']['Row'];

export type MatchWithPlayers = EventMatchSchedule & {
  player1: Player | null;
  player2: Player | null;
};

// Type for the specific fields selected in getUpcomingMatchesByEvent
export type UpcomingMatch = Pick<
  EventMatchSchedule,
  | 'id'
  | 'scheduled_datetime'
  | 'court_id'
  | 'estimated_duration_minutes'
  | 'status'
  | 'player_1_id'
  | 'player_2_id'
> & {
  player1: Player | null;
  player2: Player | null;
  event?: {
    id: string;
    name: string;
    status: string;
    start_date: string;
    end_date: string;
    settings: EventSettings;
    federation_id: string | null;
    location?: {
      id: string;
      name: string;
      address: string | null;
    } | null;
  } | null;
  event_group?: {
    id: string;
    name: string;
  } | null;
  draw_match?: Array<{
    stage?: {
      id: string;
      name: string;
    } | null;
    round?: {
      id: string;
      name: string;
      round_number: number;
    } | null;
  }> | null;
};

// Event Group types
type EventGroup = Database['public']['Tables']['event_group']['Row'];
type EventGroupInsert = Database['public']['Tables']['event_group']['Insert'];
type EventGroupUpdate = Database['public']['Tables']['event_group']['Update'];

// Price Variation types
type EventGroupPriceVariation = Database['public']['Tables']['event_group_price_variation']['Row'];
type EventGroupPriceVariationInsert =
  Database['public']['Tables']['event_group_price_variation']['Insert'];

// Event group with nested price variations
type EventGroupWithVariations = EventGroup & {
  price_variations: EventGroupPriceVariation[];
};

// Event Group Registration types
type EventGroupRegistration = Database['public']['Tables']['event_group_registration']['Row'];
type EventGroupRegistrationInsert =
  Database['public']['Tables']['event_group_registration']['Insert'];
type EventGroupRegistrationUpdate =
  Database['public']['Tables']['event_group_registration']['Update'];

type EventSettings = {
  theme: {
    primaryColor: string;
    accentColor: string;
  };
  hide_registrants_from_public?: boolean;
  /**
   * Hard-hide the event's competition surfaces (draw, matches, schedule) from
   * the public. true = all three 404 for everyone and their nav tabs/banner are
   * hidden; admins manage via the admin UI. Absent/false = visible (current
   * behavior). New events default to true (set in the admin event form).
   * See isCompetitionHidden().
   */
  hide_competition_from_public?: boolean;
  /**
   * Per-event override for whether a registered player (not an invited
   * admin/referee) may ref/score this event. `undefined` = inherit the
   * federation default (`federation.allow_registered_players_to_ref`).
   * Resolved via `resolveAllowRegisteredPlayersToRef`.
   */
  allowRegisteredPlayersToRef?: boolean;
  game_format?: {
    bestOf: 1 | 3 | 5;
    pointsPerGame: number;
    decidingGamePoints?: number | null;
    deuceRule?: 'win_by_2' | 'sudden_death';
  };
  estimated_match_duration_minutes?: number;
  kfactor?: number;
  scoringAppType?: string; // e.g. 'futsal-show', undefined = squash (default)
  matchScheduler?: {
    timeSlotDuration: number;
    startHour: number;
    endHour: number;
    courts: string[];
    defaultDuration: number;
    // New comprehensive settings structure
    courtDefinitions?: CourtDefinition[];
    globalTimeSettings?: {
      defaultStartTime: string;
      defaultEndTime: string;
    };
    daySettings?: Array<{
      date: string;
      isActive: boolean;
      courts: Array<{
        courtId: string;
        isActive: boolean;
        startTime?: string;
        endTime?: string;
      }>;
    }>;
  };
};

// Helper function to get client
function getClient(supabaseClient?: SupabaseClient<Database> | null): SupabaseClient<Database> {
  return supabaseClient || createClient();
}

// Event CRUD operations
async function getEvents(
  federationId?: string,
  status?: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<Event[]> {
  const client = getClient(supabaseClient);
  let query = client.from('event').select('*').order('start_date', { ascending: false });

  if (federationId) {
    query = query.eq('federation_id', federationId);
  }

  if (status) {
    query = query.eq('status', status);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

// The signed-in event list in one round trip (SQL get_my_event_list).
async function getMyEventList(supabaseClient?: SupabaseClient<Database> | null): Promise<Json> {
  const { data, error } = await getClient(supabaseClient).rpc('get_my_event_list');
  if (error) throw error;
  return data;
}

async function getEvent(
  id: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<Event | null> {
  const client = getClient(supabaseClient);
  const { data, error } = await client.from('event').select('*').eq('id', id).maybeSingle();

  if (error) throw error;
  return data;
}

async function createEvent(
  event: EventInsert,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<Event> {
  const client = getClient(supabaseClient);
  const { data, error } = await client.from('event').insert(event).select().single();

  if (error) throw error;
  return data;
}

async function updateEvent(
  id: string,
  updates: EventUpdate,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<Event> {
  const client = getClient(supabaseClient);
  const { data, error } = await client.from('event').update(updates).eq('id', id).select().single();

  if (error) throw error;
  return data;
}

async function deleteEvent(
  id: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<void> {
  const client = getClient(supabaseClient);
  const { error } = await client.from('event').delete().eq('id', id);

  if (error) throw error;
}

// Event Group CRUD operations
async function getEventGroups(
  eventId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventGroup[]> {
  const client = getClient(supabaseClient);
  const { data, error } = await client
    .from('event_group')
    .select('*')
    .eq('event_id', eventId)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return data || [];
}

async function getNextSortOrder(
  eventId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<number> {
  const client = getClient(supabaseClient);
  const { data, error } = await client
    .from('event_group')
    .select('sort_order')
    .eq('event_id', eventId)
    .order('sort_order', { ascending: false })
    .limit(1);

  if (error) throw error;
  return data && data.length > 0 ? (data[0].sort_order || 0) + 1 : 0;
}

async function getEventGroup(
  id: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventGroup | null> {
  const client = getClient(supabaseClient);
  const { data, error } = await client.from('event_group').select('*').eq('id', id).maybeSingle();

  if (error) throw error;
  return data;
}

async function createEventGroup(
  group: EventGroupInsert,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventGroup> {
  const client = getClient(supabaseClient);

  // If sort_order is not provided, get the next available sort order
  if (group.sort_order === undefined || group.sort_order === null) {
    group.sort_order = await getNextSortOrder(group.event_id, supabaseClient);
  }

  const { data, error } = await client.from('event_group').insert(group).select().single();

  if (error) throw error;
  return data;
}

async function updateEventGroup(
  id: string,
  updates: EventGroupUpdate,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventGroup> {
  const client = getClient(supabaseClient);
  const { data, error } = await client
    .from('event_group')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function deleteEventGroup(
  id: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<void> {
  const client = getClient(supabaseClient);
  const { error } = await client.from('event_group').delete().eq('id', id);
  if (error) throw error;
}

/**
 * Clone all event groups (divisions) from a source event into a target event.
 * This will copy all group settings while assigning them to the new event.
 * The caller is responsible for ensuring the target event is in the correct state
 * (e.g. empty) before cloning.
 */
async function cloneEventGroups(
  sourceEventId: string,
  targetEventId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventGroup[]> {
  const client = getClient(supabaseClient);

  // Load groups from source event
  const sourceGroups = await getEventGroups(sourceEventId, supabaseClient);
  if (!sourceGroups.length) {
    return [];
  }

  // Prepare cloned group rows, copying all configurable fields but assigning new event_id
  const groupsToInsert: EventGroupInsert[] = sourceGroups.map((group) => {
    // Omit fields that should not be copied directly
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, created_at, updated_at, event_id: _oldEventId, ...rest } = group;

    return {
      ...(rest as unknown as EventGroupInsert),
      event_id: targetEventId,
    };
  });

  const { data, error } = await client
    .from('event_group')
    .insert(groupsToInsert)
    .select('*')
    .order('sort_order', { ascending: true });

  if (error) throw error;
  const clonedGroups = (data || []) as EventGroup[];

  // Clone price variations: map source group IDs to new group IDs
  if (clonedGroups.length > 0) {
    const sourceGroupIds = sourceGroups.map((g) => g.id);
    const { data: sourceVariations } = await client
      .from('event_group_price_variation')
      .select('*')
      .in('event_group_id', sourceGroupIds);

    if (sourceVariations && sourceVariations.length > 0) {
      // Build mapping: source group sort_order -> cloned group id
      const sortOrderToClonedId: Record<number, string> = {};
      clonedGroups.forEach((g) => {
        sortOrderToClonedId[g.sort_order ?? 0] = g.id;
      });
      const sourceIdToSortOrder: Record<string, number> = {};
      sourceGroups.forEach((g) => {
        sourceIdToSortOrder[g.id] = g.sort_order ?? 0;
      });

      const variationsToInsert = sourceVariations
        .map((v) => {
          const sortOrder = sourceIdToSortOrder[v.event_group_id];
          const newGroupId = sortOrderToClonedId[sortOrder];
          if (!newGroupId) return null;
          return {
            event_group_id: newGroupId,
            name: v.name,
            price: v.price,
            sort_order: v.sort_order,
          };
        })
        .filter(Boolean) as EventGroupPriceVariationInsert[];

      if (variationsToInsert.length > 0) {
        await client.from('event_group_price_variation').insert(variationsToInsert);
      }
    }
  }

  return clonedGroups;
}

// Event Group Registration CRUD operations
/** A registration added mid-read shifts later pages by one row; keep the first copy of each id, in order. */
function dedupeById<T extends { id: string }>(rows: T[]): T[] {
  const seen = new Set<string>();
  return rows.filter((r) => (seen.has(r.id) ? false : (seen.add(r.id), true)));
}

async function getEventGroupRegistrations(
  eventId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventGroupRegistration[]> {
  const client = getClient(supabaseClient);
  // event_group_registration no longer has event_id — join through event_group
  const { data: groups } = await client.from('event_group').select('id').eq('event_id', eventId);

  if (!groups || groups.length === 0) return [];

  const groupIds = groups.map((g) => g.id);
  // Paged: a large event can pass PostgREST's 1,000-row cap. `id` breaks created_at ties so
  // pages neither repeat nor skip rows.
  return dedupeById(
    await selectAllRows((from, to) =>
      client
        .from('event_group_registration')
        .select(
          `
      *,
      event_group:event_group_id (
        id,
        name,
        group_type
      ),
      price_variation:price_variation_id (
        id,
        name,
        price
      ),
      player:player_id (
        id,
        username,
        first_name,
        last_name,
        first_name_furigana,
        last_name_furigana,
        current_rating,
        initial_rating,
        gender,
        profile_photo
      )
    `,
        )
        .in('event_group_id', groupIds)
        .order('created_at', { ascending: false })
        .order('id', { ascending: true })
        .range(from, to),
    ),
  );
}

async function getEventGroupRegistration(
  id: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventGroupRegistration | null> {
  const client = getClient(supabaseClient);
  const { data, error } = await client
    .from('event_group_registration')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  return data;
}

async function getEventGroupRegistrationByPlayer(
  eventId: string,
  playerId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventGroupRegistration | null> {
  const client = getClient(supabaseClient);

  // Look up groups for the event, then find registration
  const { data: groups } = await client.from('event_group').select('id').eq('event_id', eventId);

  if (!groups || groups.length === 0) return null;

  const groupIds = groups.map((g) => g.id);
  const { data, error } = await client
    .from('event_group_registration')
    .select('*')
    .in('event_group_id', groupIds)
    .eq('player_id', playerId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

async function getEventGroupRegistrationByPlayerAndGroup(
  _eventId: string,
  playerId: string,
  groupId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventGroupRegistration | null> {
  const client = getClient(supabaseClient);
  // event_group_id already scopes to the event, no need for event_id filter
  const { data, error } = await client
    .from('event_group_registration')
    .select('*')
    .eq('player_id', playerId)
    .eq('event_group_id', groupId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

async function getEventGroupRegistrationsByPlayer(
  eventId: string,
  playerId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventGroupRegistration[]> {
  const client = getClient(supabaseClient);

  // Look up groups for the event, then find registrations
  const { data: groups } = await client.from('event_group').select('id').eq('event_id', eventId);

  if (!groups || groups.length === 0) return [];

  const groupIds = groups.map((g) => g.id);
  const { data, error } = await client
    .from('event_group_registration')
    .select('*')
    .in('event_group_id', groupIds)
    .eq('player_id', playerId);

  if (error) throw error;
  return data || [];
}

async function getEventGroupRegistrationsByGroup(
  groupId: string,
): Promise<EventGroupRegistration[]> {
  const client = getSupabaseAdminClient();
  // Paged past the 1,000-row cap; `id` breaks created_at ties.
  return dedupeById(
    await selectAllRows((from, to) =>
      client!
        .from('event_group_registration')
        .select(
          `
      *,
      player:player_id (
        id,
        username,
        first_name,
        last_name,
        first_name_furigana,
        last_name_furigana,
        current_rating,
        initial_rating,
        profile_photo
      )
    `,
        )
        .eq('event_group_id', groupId)
        .order('created_at', { ascending: false })
        .order('id', { ascending: true })
        .range(from, to),
    ),
  );
}

/**
 * Registrations in the event's team divisions (`event_group.is_team = true`), with
 * the lead player's name (needed to tell an unedited default `team_name` from a
 * typed one). Feeds the public-surface team labels; see `libs/teamLabels.ts`.
 */
export type TeamRegistrationRow = {
  event_group_id: string;
  player_id: string;
  team_name: string | null;
  team_info: string | null;
  status: string | null;
  player: { first_name: string | null; last_name: string | null } | null;
  event_group: { is_team: boolean | null } | null;
};

async function getTeamRegistrationsForEvent(eventId: string): Promise<TeamRegistrationRow[]> {
  const client = getSupabaseAdminClient();
  const { data, error } = await client!
    .from('event_group_registration')
    .select(
      `
      event_group_id,
      player_id,
      team_name,
      team_info,
      status,
      player:player_id ( first_name, last_name ),
      event_group:event_group_id!inner ( is_team, event_id )
    `,
    )
    .eq('event_group.event_id', eventId)
    .eq('event_group.is_team', true)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data || []) as unknown as TeamRegistrationRow[];
}

/**
 * Everyone entered in an event, for the public Players tab and the overview's
 * players line: one row per ACTIVE registration (withdrawn entries are not
 * playing), with its division, the team fields (`team_name`, `team_info`; set
 * only in team divisions) and the player's public fields. Never selects contact
 * or account fields (email, user_id, gender, custom_data).
 *
 * Reads with the service role, so the CALLER must already have applied the
 * organiser's switch (`getCanSeeRegistrants`). `loadEventPlayers` in the event
 * feature's libs is the only caller and does so before calling this. The rows
 * stay on the server: `buildEventPlayers` decides what the browser receives.
 *
 * Paged: PostgREST caps one response at max_rows (1000 locally).
 */
export type PublicEntrantRow = {
  team_name: string | null;
  team_info: string | null;
  event_group: { id: string; name: string; sort_order: number | null; is_team: boolean } | null;
  player: {
    id: string;
    username: string | null;
    first_name: string;
    last_name: string;
    alias: string | null;
    nationality: string | null;
    current_rating: number | null;
    profile_photo: string | null;
    is_guest: boolean;
  } | null;
};

const PUBLIC_ENTRANTS_PAGE = 1000;

/**
 * Who counts as an entrant on the public surfaces: an ACTIVE `event_group_registration` in a
 * group of this event (the query must embed `event_group:event_group_id!inner`). Withdrawn or
 * otherwise inactive entries are not entrants; payment state is not consulted. The entrant
 * list and the viewer pre-check both apply it, so they cannot drift.
 */
function activeEntrantsOfEvent<Q extends { eq: (column: string, value: string) => Q }>(
  query: Q,
  eventId: string,
): Q {
  return query.eq('event_group.event_id', eventId).eq('status', 'active');
}

async function getPublicEntrantsForEvent(eventId: string): Promise<PublicEntrantRow[]> {
  const client = getSupabaseAdminClient();
  const rows: PublicEntrantRow[] = [];
  for (let from = 0; ; from += PUBLIC_ENTRANTS_PAGE) {
    const { data, error } = await activeEntrantsOfEvent(
      client!
        .from('event_group_registration')
        .select(
          `
        team_name,
        team_info,
        event_group:event_group_id!inner ( id, name, sort_order, is_team, event_id ),
        player:player_id ( id, username, first_name, last_name, alias, nationality, current_rating, profile_photo, is_guest )
      `,
        ),
      eventId,
    )
      .order('id', { ascending: true })
      .range(from, from + PUBLIC_ENTRANTS_PAGE - 1);
    if (error) throw error;
    const page = (data ?? []) as unknown as PublicEntrantRow[];
    rows.push(...page);
    if (page.length < PUBLIC_ENTRANTS_PAGE) return rows;
  }
}

/**
 * The player id under which this viewer appears in `getPublicEntrantsForEvent`, or `null` when
 * they are not an entrant — in ONE query: the same entrant filter (`activeEntrantsOfEvent`),
 * narrowed to registrations whose player row belongs to the viewer. A row comes back exactly when
 * the entrant list holds a row for that player (a person row or the lead of a team entry; a team
 * mate named only in `team_info`, like any non-entrant, gets `null`). One row per division, all
 * with the same player; two different player rows of one account → `null` (never guess).
 *
 * Reads with the service role. `verifiedUserId` MUST be the request's verified session user
 * (`getVerifiedViewerId()` / `auth.getUser()`), never a value taken from the request. Returns only
 * the viewer's own player id. Throws on a read error, as the entrant list does.
 */
async function getViewerEntrantPlayerId(
  eventId: string,
  verifiedUserId: string,
): Promise<string | null> {
  const client = getSupabaseAdminClient();
  const { data, error } = await activeEntrantsOfEvent(
    client!
      .from('event_group_registration')
      .select('player_id, event_group:event_group_id!inner ( event_id ), player:player_id!inner ( user_id )'),
    eventId,
  ).eq('player.user_id', verifiedUserId);
  if (error) throw error;
  const ids = new Set((data ?? []).map((row) => row.player_id).filter((id): id is string => !!id));
  return ids.size === 1 ? [...ids][0] : null;
}

async function isViewerRegisteredForEvent(
  supa: SupabaseClient<Database>,
  eventId: string,
  userId: string | null,
): Promise<boolean> {
  if (!userId) return false;

  const { data: player } = await supa
    .from('player')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();
  if (!player?.id) return false;

  const { transactionModel } = await import('@/models/transactionModel');
  return transactionModel.hasActiveRegistration(eventId, player.id);
}

async function getCanSeeRegistrants(
  supa: SupabaseClient<Database>,
  event: { id: string } & RegistrantVisibilityEvent,
  userId: string | null,
): Promise<boolean> {
  if (!hidesRegistrantsFromPublic(event)) return true;

  return isViewerRegisteredForEvent(supa, event.id, userId);
}

async function createEventGroupRegistration(
  registration: EventGroupRegistrationInsert,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventGroupRegistration> {
  const client = getClient(supabaseClient);
  const { data, error } = await client
    .from('event_group_registration')
    .insert(registration)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function updateEventGroupRegistration(
  id: string,
  updates: EventGroupRegistrationUpdate,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventGroupRegistration> {
  const client = getClient(supabaseClient);
  const { data, error } = await client
    .from('event_group_registration')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      throw new Error('Registration not found');
    }
    throw error;
  }

  if (!data) {
    throw new Error('Registration not found');
  }

  return data;
}

async function deleteEventGroupRegistration(
  id: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<void> {
  const client = getClient(supabaseClient);
  const { error } = await client.from('event_group_registration').delete().eq('id', id);
  if (error) throw error;
}

async function unlinkRegistrationLineItems(
  registrationId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<Database['public']['Tables']['purchase_line_item']['Row'][]> {
  const client = getClient(supabaseClient);

  // Find line items referencing this registration
  const { data: lineItems, error: fetchError } = await client
    .from('purchase_line_item')
    .select('*')
    .eq('event_group_registration_id', registrationId);

  if (fetchError) throw fetchError;
  if (!lineItems || lineItems.length === 0) return [];

  // Delete the line items (can't null the FK due to exactly-one-type check constraint)
  // and append audit notes to parent transactions
  for (const item of lineItems) {
    const { error: deleteError } = await client
      .from('purchase_line_item')
      .delete()
      .eq('id', item.id);

    if (deleteError) throw deleteError;

    // Append note to the parent transaction
    const { data: tx, error: txFetchError } = await client
      .from('purchase_transaction')
      .select('notes')
      .eq('id', item.transaction_id)
      .single();

    if (txFetchError) throw txFetchError;

    const note = `[Admin] Registration removed — line item "${item.description}" ($${Number(item.unit_price).toFixed(2)}) deleted`;
    const updatedNotes = tx.notes ? `${tx.notes}\n${note}` : note;

    const { error: txUpdateError } = await client
      .from('purchase_transaction')
      .update({ notes: updatedNotes })
      .eq('id', item.transaction_id);

    if (txUpdateError) throw txUpdateError;
  }

  return lineItems;
}

// Get available players for registration
async function getAvailablePlayers(
  eventId: string,
  search?: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<
  {
    id: string;
    username: string | null;
    first_name: string;
    last_name: string;
    current_rating: number | null;
    created_at: string;
  }[]
> {
  const client = getClient(supabaseClient);
  // Get event details to check multiple groups setting
  const event = await getEvent(eventId, client);
  if (!event) throw new Error('Event not found');

  // Get all players
  let query = client
    .from('player')
    .select(
      `
      id,
      username,
      first_name,
      last_name,
      current_rating,
      created_at
    `,
    )
    .order('current_rating', { ascending: false });

  // Add search filter if provided
  if (search) {
    query = query.or(
      `username.ilike.%${search}%,first_name.ilike.%${search}%,last_name.ilike.%${search}%`,
    );
  }

  const { data: players, error } = await query;
  if (error) throw error;

  // Return all players - validation should happen when trying to register them
  return players || [];
}

/**
 * Verifies that a user can score matches for an event
 * Checks all 4 permission types:
 * 1. Federation admin of event's federation
 * 2. Event admin
 * 3. Event referee
 * 4. Registered in event (via event_group_registration)
 */
async function verifyUserCanScoreEvent(
  eventId: string,
  userId: string,
  supabaseClient?: SupabaseClient<Database> | null,
  options?: {
    federationIds?: string[];
    eventIds?: string[];
    playerId?: string;
    eventFederationId?: string;
  },
): Promise<boolean> {
  const client = getClient(supabaseClient);

  // Get event federation_id if not provided
  let eventFederationId = options?.eventFederationId;
  if (!eventFederationId) {
    const { data: event, error: eventError } = await client
      .from('event')
      .select('federation_id')
      .eq('id', eventId)
      .maybeSingle();

    if (eventError) throw eventError;
    if (!event) return false;
    eventFederationId = event.federation_id;
  }

  // Get user's player record if not provided
  let playerId = options?.playerId;
  if (!playerId) {
    const { data: player, error: playerError } = await client
      .from('player')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();

    if (playerError) throw playerError;
    if (!player) return false;
    playerId = player.id;
  }

  // Check 1: Federation admin of event's federation (if federation IDs provided)
  if (options?.federationIds?.includes(eventFederationId)) {
    return true;
  }

  // Check 2 & 3: Event admin or referee (if event IDs provided)
  if (options?.eventIds?.includes(eventId)) {
    return true;
  }

  // Check 4: Registered in event via event_group_registration
  // We need to find groups for this event, then check if any registration exists
  const { data: groups } = await client.from('event_group').select('id').eq('event_id', eventId);

  if (!groups || groups.length === 0) return false;

  const groupIds = groups.map((g) => g.id);
  const { data: registration } = await client
    .from('event_group_registration')
    .select('id')
    .eq('player_id', playerId)
    .in('event_group_id', groupIds)
    .limit(1)
    .maybeSingle();

  return registration !== null;
}

// Get participant count for an event group dynamically
async function getEventGroupParticipantCount(groupId: string): Promise<number> {
  const client = getClient();
  const { count, error } = await client
    .from('event_group_registration')
    .select('*', { count: 'exact', head: true })
    .eq('event_group_id', groupId);

  if (error) throw error;
  return count || 0;
}

// Get participant count for an event dynamically
// Counts distinct players across all groups in the event
async function getEventParticipantCount(eventId: string): Promise<number> {
  const client = getClient();

  // Get all group IDs for the event
  const { data: groups, error: groupError } = await client
    .from('event_group')
    .select('id')
    .eq('event_id', eventId);

  if (groupError) throw groupError;
  if (!groups || groups.length === 0) return 0;

  const groupIds = groups.map((g) => g.id);
  const { data, error } = await client
    .from('event_group_registration')
    .select('player_id')
    .in('event_group_id', groupIds);

  if (error) throw error;

  // Count distinct players
  const uniquePlayers = new Set((data || []).map((r) => r.player_id));
  return uniquePlayers.size;
}

/** One row per registration: the event it belongs to and the player. */
export type EventRegistrationPlayerRow = { event_id: string; player_id: string };

// (event_id, player_id) for every registration in the given events, any status — the rows
// getEventParticipantCount reads for one event, for a whole list in one go. Ids are chunked
// (URL length) and rows paged (max_rows), so a long list is neither refused nor truncated.
async function getRegistrationPlayersForEvents(
  eventIds: readonly string[],
  supabaseClient: SupabaseClient<Database>,
): Promise<EventRegistrationPlayerRow[]> {
  const rows = await inChunks(eventIds, async (chunk) => ({
    data: await selectAllRows((from, to) =>
      supabaseClient
        .from('event_group_registration')
        .select('id, player_id, event_group!inner(event_id)')
        .in('event_group.event_id', chunk)
        .order('id', { ascending: true })
        .range(from, to),
    ),
    error: null,
  }));
  return rows.map((r) => ({ event_id: r.event_group.event_id, player_id: r.player_id }));
}

/**
 * Whether a division belongs to the event — a division id from another event must not be
 * readable through this event's permission. Throws on a query error (never answers "no").
 */
async function groupBelongsToEvent(
  groupId: string,
  eventId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<boolean> {
  const client = getClient(supabaseClient);
  const { data, error } = await client
    .from('event_group')
    .select('id')
    .eq('id', groupId)
    .eq('event_id', eventId)
    .maybeSingle();
  if (error) throw error;
  return data !== null;
}

async function hasPlayedMatches(
  eventId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<boolean> {
  const client = getClient(supabaseClient);
  const { count, error } = await client
    .from('event_match_schedule')
    .select('match:match_id!inner(status)', { count: 'exact', head: true })
    .eq('event_id', eventId)
    .eq('match.status', 'completed');

  if (error) throw error;
  return (count ?? 0) > 0;
}

// Static utility method for generating public event URLs
function generatePublicUrl(eventId: string, eventSlug: string, locale: string = 'en'): string {
  if (!eventId || !eventSlug) return '';

  // The public event page always lives on the app host (app.playpulse.io) — never
  // the club portal host. Prefer the configured app URL (inlined client-side via
  // NEXT_PUBLIC_APP_URL); fall back to the current origin in dev / when unset. On
  // the admin host these are identical, so this is a no-op there.
  const origin =
    typeof window !== 'undefined'
      ? process.env.NEXT_PUBLIC_APP_URL || window.location.origin
      : getBaseUrl();

  return `${origin}/${locale}/event/${eventId}/${eventSlug}`;
}

// Utility functions for event settings
function getEventGameFormat(settings: EventSettings | null | undefined) {
  const defaultFormat = { bestOf: 5 as const, pointsPerGame: 11 as const };

  if (!settings?.game_format) {
    return defaultFormat;
  }

  return {
    bestOf: settings.game_format.bestOf || defaultFormat.bestOf,
    pointsPerGame: settings.game_format.pointsPerGame || defaultFormat.pointsPerGame,
    decidingGamePoints: settings.game_format.decidingGamePoints ?? null,
  };
}

function getEventKFactor(settings: EventSettings | null | undefined) {
  return settings?.kfactor || 30.0;
}

/**
 * Get federation k_factor by federation ID
 */
async function getFederationKFactor(
  federationId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<number> {
  const client = getClient(supabaseClient);

  const { data: federation, error: federationError } = await client
    .from('federation')
    .select('k_factor')
    .eq('id', federationId)
    .maybeSingle();

  if (federationError) throw federationError;
  return federation?.k_factor || 30.0;
}

/**
 * Resolve k_factor for event with priority:
 * 1. Event settings k_factor (if exists)
 * 2. Federation k_factor (if event belongs to federation)
 * 3. Default 30
 */
async function resolveKFactorForEvent(
  eventId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<number> {
  const client = getClient(supabaseClient);

  // Get event with federation info
  const { data: event, error: eventError } = await client
    .from('event')
    .select(
      `
      id,
      settings,
      federation_id,
      federation:federation_id (
        k_factor
      )
    `,
    )
    .eq('id', eventId)
    .maybeSingle();

  if (eventError) throw eventError;
  if (!event) return 30.0; // Default if event not found

  // 1. Check event settings k_factor first
  const eventSettings = event.settings as EventSettings | null;
  if (eventSettings?.kfactor) {
    return eventSettings.kfactor;
  }

  // 2. Fall back to federation k_factor
  if (event.federation_id && event.federation?.k_factor) {
    return event.federation.k_factor;
  }

  // 3. Default fallback
  return 30.0;
}

// Get upcoming matches for an event
async function getUpcomingMatchesByEvent(
  eventId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<UpcomingMatch[]> {
  const client = getClient(supabaseClient);

  const { data, error } = await client
    .from('event_match_schedule')
    .select(
      `
      id,
      scheduled_datetime,
      court_id,
      estimated_duration_minutes,
      status,
      match(id, status),
      player_1_id,
      player_2_id,
      player1:player!event_match_schedule_player_1_id_fkey (
        id,
        first_name,
        last_name,
        username,
        alias,
        current_rating,
        profile_photo
      ),
      player2:player!event_match_schedule_player_2_id_fkey (
        id,
        first_name,
        last_name,
        username,
        alias,
        current_rating,
        profile_photo
      ),
      event:event_id (
        id,
        name,
        status,
        start_date,
        end_date,
        settings,
        federation_id,
        location:location_id (
          id,
          name,
          address
        )
      ),
      event_group:event_group_id (
        id,
        name
      ),
      draw_match:draw_match!event_match_schedule_id (
        stage:stage_id (
          id,
          name
        ),
        round:round_id (
          id,
          name,
          round_number
        )
      )
    `,
    )
    .eq('event_id', eventId)
    .eq('event.status', 'published')
    .in('status', ['scheduled', 'in_progress'])
    .order('scheduled_datetime', { ascending: true });

  if (error) throw error;
  const matches = (data.filter((match) => match.match?.status !== 'completed') || []).map(
    (match) => ({
      ...match,
      event: match.event
        ? {
            ...match.event,
            settings: match.event.settings as EventSettings,
          }
        : undefined,
    }),
  ) as UpcomingMatch[];
  return gateRowAliases(matches, ['player1', 'player2'], () =>
    isEventAliasAllowed(eventId, client),
  );
}

// Virtual status calculation based on dates and event state
type VirtualEventStatus =
  | 'draft'
  | 'published'
  | 'cancelled'
  | 'registration_open'
  | 'registration_closed'
  | 'in_progress'
  | 'completed';

interface EventWithVirtualStatus extends Event {
  virtualStatus: VirtualEventStatus;
}

/**
 * Calculate the virtual status of an event based on its core status and dates
 * This replaces the need for multiple database status values
 */
export function calculateVirtualEventStatus(
  event: Pick<
    Event,
    'status' | 'start_date' | 'end_date' | 'registration_opens' | 'registration_closes'
  >,
): VirtualEventStatus {
  const now = new Date();
  const startDate = new Date(event.start_date);
  const endDate = new Date(event.end_date);
  const registrationOpens = event.registration_opens ? new Date(event.registration_opens) : null;
  const registrationCloses = event.registration_closes ? new Date(event.registration_closes) : null;

  // If event is cancelled, that takes precedence
  if (event.status === 'cancelled') {
    return 'cancelled';
  }

  // If event is draft, that takes precedence
  if (event.status === 'draft') {
    return 'draft';
  }

  // For published events, calculate virtual status based on dates
  if (event.status === 'published') {
    // Check if event is completed
    if (endDate < now) {
      return 'completed';
    }

    // Check if event is in progress
    if (startDate <= now && endDate >= now) {
      return 'in_progress';
    }

    // Check registration status
    if (registrationOpens && registrationCloses) {
      if (now >= registrationOpens && now <= registrationCloses) {
        return 'registration_open';
      } else if (now > registrationCloses) {
        return 'registration_closed';
      }
    }

    // Default for published events that haven't started
    return 'published';
  }

  // Fallback
  return 'draft';
}

/**
 * Get court name from event settings
 */
export function getCourtName(
  courtId: string | null,
  eventSettings: EventSettings | null | undefined,
): string {
  if (!courtId) return '';

  // Try to find court definition by ID in new structure
  const courtDef = eventSettings?.matchScheduler?.courtDefinitions?.find((c) => c.id === courtId);
  if (courtDef) {
    return courtDef.name;
  }

  // Fallback to extracting number from court ID (e.g., "court-1" -> "Court 1")
  const courtNumber = courtId.replace('court-', '');
  return `Court ${courtNumber}`;
}

/**
 * Get events with virtual statuses calculated
 */
async function getEventsWithVirtualStatus(
  federationId?: string,
  status?: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventWithVirtualStatus[]> {
  const events = await getEvents(federationId, status, supabaseClient);
  return events.map((event) => ({
    ...event,
    virtualStatus: calculateVirtualEventStatus(event),
  }));
}

/**
 * Get a single event with virtual status calculated
 */
async function getEventWithVirtualStatus(
  id: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventWithVirtualStatus | null> {
  const event = await getEvent(id, supabaseClient);
  if (!event) return null;

  return {
    ...event,
    virtualStatus: calculateVirtualEventStatus(event),
  };
}

// Get event match schedule by ID
async function getEventMatchSchedule(
  id: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventMatchSchedule | null> {
  const client = getClient(supabaseClient);
  const { data, error } = await client
    .from('event_match_schedule')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  return data;
}

// Get event match schedule by match ID
async function getEventMatchScheduleByMatchId(
  matchId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventMatchSchedule | null> {
  const client = getClient(supabaseClient);
  const { data, error } = await client
    .from('event_match_schedule')
    .select('*')
    .eq('match_id', matchId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

// Get event information for rating purposes (with location and group details)
async function getEventInfoForRating(
  eventId: string,
  eventGroupId: string | null,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<{
  event_type: string;
  event_name: string;
  event_location_name: string | null;
  event_division_name: string | null;
} | null> {
  const client = getClient(supabaseClient);

  const { data: event, error: eventError } = await client
    .from('event')
    .select(
      `
      event_type,
      name,
      location:location_id (
        name
      )
    `,
    )
    .eq('id', eventId)
    .maybeSingle();

  if (eventError) throw eventError;
  if (!event) return null;

  let eventDivisionName: string | null = null;

  if (eventGroupId) {
    const { data: eventGroup, error: groupError } = await client
      .from('event_group')
      .select('name')
      .eq('id', eventGroupId)
      .maybeSingle();

    if (groupError) throw groupError;
    eventDivisionName = eventGroup?.name || null;
  }

  return {
    event_type: event.event_type,
    event_name: event.name,
    event_location_name: event.location?.name || null,
    event_division_name: eventDivisionName,
  };
}

// Get upcoming matches for a specific player across all events
async function getUpcomingMatchesByPlayer(
  playerId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<UpcomingMatch[]> {
  const client = getClient(supabaseClient);

  const { data, error } = await client
    .from('event_match_schedule')
    .select(
      `
      id,
      scheduled_datetime,
      court_id,
      estimated_duration_minutes,
      status,
      match(id, status),
      player_1_id,
      player_2_id,
      event:event_id (
        id,
        name,
        status,
        start_date,
        end_date,
        settings,
        location:location_id (
          id,
          name,
          address
        )
      ),
      event_group:event_group_id (
        id,
        name
      ),
      player1:player!event_match_schedule_player_1_id_fkey (
        id,
        first_name,
        last_name,
        username,
        current_rating,
        profile_photo
      ),
      player2:player!event_match_schedule_player_2_id_fkey (
        id,
        first_name,
        last_name,
        username,
        current_rating,
        profile_photo
      )
    `,
    )
    .or(`player_1_id.eq.${playerId},player_2_id.eq.${playerId}`)
    .in('status', ['scheduled', 'in_progress'])
    .gte('scheduled_datetime', new Date().toISOString())
    .order('scheduled_datetime', { ascending: true });

  if (error) throw error;

  // Filter out matches from unpublished events and completed matches
  return (data || [])
    .filter((match) => {
      const event = match.event as { status?: string } | null;
      return event?.status === 'published' && match.match?.status !== 'completed';
    })
    .map((match) => ({
      id: match.id,
      scheduled_datetime: match.scheduled_datetime,
      court_id: match.court_id,
      estimated_duration_minutes: match.estimated_duration_minutes,
      status: match.status,
      player_1_id: match.player_1_id,
      player_2_id: match.player_2_id,
      player1: match.player1,
      player2: match.player2,
      event: match.event,
      event_group: match.event_group,
    })) as UpcomingMatch[];
}

// Price Variation operations

/**
 * Fetch event groups for an event with their price variations nested.
 */
async function getEventGroupsWithVariations(
  eventId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventGroupWithVariations[]> {
  const client = getClient(supabaseClient);

  // Fetch groups
  const groups = await getEventGroups(eventId, client);

  if (groups.length === 0) return [];

  // Fetch all variations for these groups in one query
  const groupIds = groups.map((g) => g.id);
  const { data: variations, error } = await client
    .from('event_group_price_variation')
    .select('*')
    .in('event_group_id', groupIds)
    .order('sort_order', { ascending: true });

  if (error) throw error;

  // Group variations by event_group_id
  const variationsByGroup: Record<string, EventGroupPriceVariation[]> = {};
  for (const v of variations || []) {
    if (!variationsByGroup[v.event_group_id]) {
      variationsByGroup[v.event_group_id] = [];
    }
    variationsByGroup[v.event_group_id].push(v);
  }

  return groups.map((group) => ({
    ...group,
    price_variations: variationsByGroup[group.id] || [],
  }));
}

/**
 * Upsert price variations for a group.
 * Inserts new, updates existing, deletes removed (unless they have registrations).
 */
async function upsertEventGroupPriceVariations(
  groupId: string,
  variations: Array<{ id?: string; name: string; price: number; sort_order?: number }>,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventGroupPriceVariation[]> {
  const client = getClient(supabaseClient);

  // Get existing variations
  const { data: existing, error: fetchError } = await client
    .from('event_group_price_variation')
    .select('id')
    .eq('event_group_id', groupId);

  if (fetchError) throw fetchError;

  const existingIds = new Set((existing || []).map((v) => v.id));
  // Only an id this group already owns is an update. Any other id is treated as a new
  // option and given a fresh id: `onConflict: 'id'` would otherwise overwrite — and
  // re-parent — another event's price option.
  const ownId = (v: { id?: string }) => (v.id && existingIds.has(v.id) ? v.id : null);
  const incomingIds = new Set(variations.map(ownId).filter((id): id is string => id !== null));

  // IDs to delete: existing but not in incoming
  const toDelete = [...existingIds].filter((id) => !incomingIds.has(id));

  // Protect variations with existing registrations from deletion
  if (toDelete.length > 0) {
    const { data: usedVariations } = await client
      .from('event_group_registration')
      .select('price_variation_id')
      .in('price_variation_id', toDelete);

    const usedIds = new Set((usedVariations || []).map((r) => r.price_variation_id));
    const safeToDelete = toDelete.filter((id) => !usedIds.has(id));

    if (safeToDelete.length > 0) {
      const { error: deleteError } = await client
        .from('event_group_price_variation')
        .delete()
        .in('id', safeToDelete);
      if (deleteError) throw deleteError;
    }
  }

  // Upsert: insert new and update existing.
  // Always supply an id — mixing rows with/without id in a single upsert
  // sends id: null for the missing ones and bypasses the DB default.
  const toUpsert: EventGroupPriceVariationInsert[] = variations.map((v, index) => ({
    id: ownId(v) ?? crypto.randomUUID(),
    event_group_id: groupId,
    name: v.name,
    price: v.price,
    sort_order: v.sort_order ?? index,
  }));

  if (toUpsert.length > 0) {
    const { error: upsertError } = await client
      .from('event_group_price_variation')
      .upsert(toUpsert, { onConflict: 'id' });
    if (upsertError) throw upsertError;
  }

  // Return the final state
  const { data: result, error: resultError } = await client
    .from('event_group_price_variation')
    .select('*')
    .eq('event_group_id', groupId)
    .order('sort_order', { ascending: true });

  if (resultError) throw resultError;
  return result || [];
}

/**
 * Get price variations for a single group.
 */
async function getEventGroupPriceVariations(
  groupId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventGroupPriceVariation[]> {
  const client = getClient(supabaseClient);
  const { data, error } = await client
    .from('event_group_price_variation')
    .select('*')
    .eq('event_group_id', groupId)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return data || [];
}

/**
 * Do this event's matches count towards (and its pages show) ratings? Off
 * when the event itself has `ratings_enabled = false` or its federation
 * does. One rule, owned by the SQL function `ratings_enabled_for_event`
 * (shared with the mobile app's producers). No event / no federation →
 * true, as before.
 *
 * `finalizeMatch` uses this answer to decide whether to WRITE a rating row
 * at all, so an RPC error here must not silently fall back to "on": that
 * would rate matches in an off federation for as long as the RPC keeps
 * erroring (e.g. `20260923090000` not yet applied on this environment
 * while this code is already live). On RPC error, fall back to the direct
 * event + federation read the rule replaced; only if THAT also errors do we return
 * true (with the error logged, as before).
 */
async function isEventRatingsEnabled(
  eventId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<boolean> {
  const client = getClient(supabaseClient);
  const { data, error } = await client.rpc('ratings_enabled_for_event', { p_event_id: eventId });
  if (!error) {
    return data !== false;
  }

  const { data: event, error: fallbackError } = await client
    .from('event')
    .select('ratings_enabled, federation:federation_id(ratings_enabled)')
    .eq('id', eventId)
    .maybeSingle();

  if (fallbackError) {
    console.error(
      '[eventModel] ratings_enabled_for_event failed, and the direct federation fallback also failed',
      error,
      fallbackError,
    );
    return true;
  }

  if ((event as any)?.ratings_enabled === false) return false;
  const federation = (event as any)?.federation;
  return federation?.ratings_enabled !== false;
}

/**
 * What a finished match in this event writes to the rating table.
 *
 * - `rated`: a normal rating row.
 * - `history_only`: the event's own switch is off inside a rated federation.
 *   The match is still recorded (zero change, null K-factor) so it shows in
 *   player history, but it moves no rating.
 * - `none`: the federation has ratings off; nothing is written, as before.
 *
 * The on/off answer is `isEventRatingsEnabled`; the federation read only
 * tells the two "off" cases apart, and a failed read stays on `none` (the
 * pre-existing behaviour for an off answer).
 */
type EventRatingWrite = 'rated' | 'history_only' | 'none';

async function resolveEventRatingWrite(
  eventId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<EventRatingWrite> {
  if (await isEventRatingsEnabled(eventId, supabaseClient)) return 'rated';

  const client = getClient(supabaseClient);
  const { data: event, error } = await client
    .from('event')
    .select('federation:federation_id(ratings_enabled)')
    .eq('id', eventId)
    .maybeSingle();
  if (error || !event) return 'none';

  const federation = (event as any)?.federation;
  return federation?.ratings_enabled === false ? 'none' : 'history_only';
}

/**
 * The single web-side reader of the federation `allow_alias` rule
 * (`public.alias_allowed_for_event`, migration 20261004160000). Display-only,
 * so any failure answers "allowed" (today's behaviour) rather than hiding names.
 */
async function isEventAliasAllowed(
  eventId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<boolean> {
  const client = getClient(supabaseClient);
  const { data, error } = await client.rpc('alias_allowed_for_event', { p_event_id: eventId });
  if (error) {
    console.error('[eventModel] alias_allowed_for_event failed', error);
    return true;
  }
  return data !== false;
}

/**
 * The player-scoped side of the same rule (`public.alias_allowed_for_player`):
 * any active federation membership that allows aliases, none -> allowed. Used
 * where no event is in scope (an ad-hoc match, the player's own profile).
 */
async function isPlayerAliasAllowed(
  playerId: string,
  supabaseClient?: SupabaseClient<Database> | null,
): Promise<boolean> {
  const client = getClient(supabaseClient);
  const { data, error } = await client.rpc('alias_allowed_for_player', { p_player_id: playerId });
  if (error) {
    console.error('[eventModel] alias_allowed_for_player failed', error);
    return true;
  }
  return data !== false;
}

/**
 * Lightweight query for sitemap generation — returns only fields needed for URL building.
 * Uses the provided client (should be admin client to bypass RLS).
 */
async function getPublishedEventSlugs(
  supabaseClient: SupabaseClient<Database>,
): Promise<Array<{ id: string; slug: string; updated_at: string | null }>> {
  const { data, error } = await supabaseClient
    .from('event')
    .select('id, slug, updated_at')
    .eq('status', 'published')
    .order('start_date', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Export all functions
export {
  // Event functions
  getEvents,
  getMyEventList,
  getEvent,
  getEventsWithVirtualStatus,
  getEventWithVirtualStatus,
  createEvent,
  updateEvent,
  deleteEvent,

  // Event Group functions
  getEventGroups,
  getEventGroup,
  createEventGroup,
  updateEventGroup,
  deleteEventGroup,
  cloneEventGroups,

  // Event Group Registration functions
  getEventGroupRegistrations,
  getEventGroupRegistration,
  getEventGroupRegistrationByPlayer,
  getEventGroupRegistrationByPlayerAndGroup,
  getEventGroupRegistrationsByPlayer,
  getEventGroupRegistrationsByGroup,
  getTeamRegistrationsForEvent,
  getPublicEntrantsForEvent,
  getViewerEntrantPlayerId,
  isViewerRegisteredForEvent,
  getCanSeeRegistrants,
  createEventGroupRegistration,
  updateEventGroupRegistration,
  deleteEventGroupRegistration,
  unlinkRegistrationLineItems,
  getEventGroupParticipantCount,
  getEventParticipantCount,
  groupBelongsToEvent,
  getRegistrationPlayersForEvents,
  hasPlayedMatches,
  getAvailablePlayers,
  generatePublicUrl,
  getPublishedEventSlugs,
  getEventGameFormat,
  getEventKFactor,
  getFederationKFactor,
  resolveKFactorForEvent,
  verifyUserCanScoreEvent,
  getUpcomingMatchesByEvent,
  getUpcomingMatchesByPlayer,
  getEventMatchSchedule,
  getEventMatchScheduleByMatchId,
  getEventInfoForRating,
  getEventGroupsWithVariations,
  upsertEventGroupPriceVariations,
  getEventGroupPriceVariations,
  isEventRatingsEnabled,
  isEventAliasAllowed,
  isPlayerAliasAllowed,
  resolveEventRatingWrite,
};

// Export types
export type {
  CourtDefinition,
  Event,
  EventInsert,
  EventUpdate,
  EventSettings,
  VirtualEventStatus,
  EventWithVirtualStatus,
  EventGroup,
  EventGroupInsert,
  EventGroupUpdate,
  EventGroupRegistration,
  EventGroupRegistrationInsert,
  EventGroupRegistrationUpdate,
  EventGroupPriceVariation,
  EventGroupWithVariations,
};
